import type { i18n } from 'i18next';

import normalizeLanguage from '../normalizeLanguage';

/** Instances whose `changeLanguage` is already wrapped. */
const normalized = new WeakSet<i18n>();

/**
 * Makes `instance.changeLanguage` normalize every language code it receives
 * with {@link normalizeLanguage} before i18next sees it.
 *
 * i18next does not alias languages: it replaces a code it does not support
 * with the fallback. So `nb-NO`, the code a Norwegian device reports, renders
 * English even in an app that ships `no`. Apps pass a language to
 * `changeLanguage` from many places (the native host config, a JWT,
 * `navigator.language`, `window.nativeLanguage`), and normalizing at each
 * call site has missed some every time (FE-3182, FE-3198). Wrapping the one
 * method they all go through covers the existing call sites and any new one.
 *
 * Only a string argument is normalized; `undefined` still lets the detector
 * pick the language. Calling this twice on one instance wraps it once.
 *
 * @param instance - The i18next instance to patch, before its `init`, which
 * itself calls `changeLanguage` for the initial language.
 */
export default function normalizeChangeLanguage(instance: i18n): void {
  if (normalized.has(instance)) return;
  normalized.add(instance);

  const changeLanguage = instance.changeLanguage.bind(instance);

  instance.changeLanguage = ((lng, callback) =>
    changeLanguage(
      typeof lng === 'string' ? normalizeLanguage(lng) : lng,
      callback,
    )) as i18n['changeLanguage'];
}
